import { t } from "i18next";
import { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { Video } from "../../interfaces/video";
import { getVideos } from "../../services/video";
import { LoadingSpinner } from "./loading-spinner";

interface VideoSelectorProps {
    videos: Video[];
    acceptCallback: CallableFunction;
    rejectCallback: CallableFunction;
}

export function VideoSelector(props: VideoSelectorProps) {
    const [loaded, setLoaded] = useState(false);
    const [videos, setVideos] = useState<Video[]>([]);
    const [selectedVideos, setSelectedVideos] = useState(props.videos.map((v: Video) => { return v.id; }));
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [searchParams, setSearchParams] = useState({
        search: ''
    });

    useEffect(() => {
        getVideos('', 1, 12)
            .then(response => {
                const videosData = response.data;
                setVideos([
                    ...props.videos,
                    ...videosData.filter((v: Video) => !selectedVideos.includes(v.id))
                ]);
                setHasMore(videosData.length === 12);
                setLoaded(true);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    const handleSearchSubmit = (event: any) => {
        event.preventDefault();

        const s = event.target.search.value;

        setSearchParams({ search: s });
        setCurrentPage(1);

        getVideos(s, 1, 12)
            .then(response => {
                const videosData = response.data;
                setVideos([
                    ...videosData.filter((v: Video) => selectedVideos.includes(v.id)),
                    ...videosData.filter((v: Video) => !selectedVideos.includes(v.id))
                ]);
                setHasMore(videosData.length === 12);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleLoadMore = (event: any) => {
        event.preventDefault();
        const nextPage = currentPage + 1;

        getVideos(searchParams.search, nextPage, 12)
            .then(response => {
                const newVideos = response.data;
                setVideos([...videos, ...newVideos.filter((v: Video) => !videos.some((existing: Video) => existing.id === v.id))]);
                setCurrentPage(nextPage);
                setHasMore(newVideos.length === 12);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleAccept = () => {
        props.acceptCallback(selectedVideos);
    }

    const handleClose = () => {
        props.rejectCallback();
    }

    const handleVideoSeleted = (event: any) => {
        const value = event.currentTarget.value;
        const checked = event.currentTarget.checked;
        if (checked) {
            setSelectedVideos([
                ...selectedVideos,
                value
            ]);
        } else {
            setSelectedVideos(selectedVideos.filter((v: number) => v != value));
        }
    }

    return (
        <Modal className="video-selector-modal" show={ true }
            onHide={ handleClose } size="xl"> 
            <Modal.Header closeButton>
                <Modal.Title>
                    { t("video_selector") }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                { !loaded && 
                    <LoadingSpinner />
                }
                { loaded && 
                    <div className="video-selector-container">
                        <div className="filters-container">
                            <Form onSubmit={ handleSearchSubmit }>
                                <Form.Group className="field-search" controlId="search">
                                    <Form.Control type="search" placeholder={ t("Write to search") } />
                                </Form.Group>
                                <div className="actions">
                                    <Button variant="primary" type="submit">
                                        { t("search") }
                                    </Button>
                                </div>
                            </Form>
                        </div>
                        <div className="grid-container">
                            <Row>
                                { videos.map((v: Video) => {
                                    return <Col key={ 'video-' + v.id } xs={12} md={6} lg={4} xl={3}>
                                        <div className={ 'video-card' + (selectedVideos.includes(v.id) ? ' selected' : '') }>
                                            <div className="media">
                                                { v.media.length > 0 &&
                                                    <video controls>
                                                        <source src={ v.media[0].url } />
                                                    </video>
                                                }
                                                <div className="selector">
                                                    <input type="checkbox" value={ v.id } checked={ selectedVideos.includes(v.id) ? true : false }
                                                        onChange={ handleVideoSeleted } />
                                                </div>
                                            </div>
                                            <div className="meta">
                                                <div className="name">
                                                    { v.name }
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                })}
                            </Row>
                            { hasMore &&
                                <div className="load-more-container" style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <Button variant="primary" onClick={ handleLoadMore }>
                                        { t("Load more") }
                                    </Button>
                                </div>
                            }
                        </div>
                        <div className="actions">
                            <Button variant="primary" onClick={ handleAccept }>
                                { t("Save") }
                            </Button>
                            <Button variant="secondary" onClick={ handleClose }>
                                { t("Close") }
                            </Button>
                        </div>
                    </div>
                }
            </Modal.Body>
        </Modal>
    )
}
